const stages = [
  'capture-start',
  'mic-open',
  'permission',
  'capture-stop',
  'transcribe',
  'send',
  'reply',
  'playback-start',
  'playback-stop',
] as const;
const outcomes = ['started', 'succeeded', 'failed', 'cancelled'] as const;
const codes = [
  'none',
  'permission-denied',
  'unavailable',
  'timeout',
  'cancelled',
  'network-failure',
  'unknown-failure',
] as const;
export type CallDiagnosticStage = (typeof stages)[number];
export type CallDiagnosticOutcome = (typeof outcomes)[number];
export type CallDiagnosticCode = (typeof codes)[number];
export interface CallDiagnosticEvent {
  sequence: number;
  stage: CallDiagnosticStage;
  outcome: CallDiagnosticOutcome;
  code: CallDiagnosticCode;
  elapsedMs: number;
  durationMs: number;
}
export interface CallDiagnosticSink {
  create(signal: AbortSignal): Promise<string>;
  link?(
    id: string,
    sequence: number,
    conversationId: string,
    eventTurnId: number,
    signal: AbortSignal
  ): Promise<void>;
  append(
    id: string,
    events: readonly CallDiagnosticEvent[],
    signal: AbortSignal
  ): Promise<void>;
}

/** Content-free, bounded, best-effort reporting. A failed sink disables this instance. */
export class DirectCallDiagnostics {
  private readonly sink: CallDiagnosticSink;
  private readonly now: () => number;
  private readonly started: number;
  private readonly abort = new AbortController();
  private readonly pending: CallDiagnosticEvent[] = [];
  private readonly links: {
    sequence: number;
    conversationId: string;
    eventTurnId: number;
  }[] = [];
  private capture: number[] = [];
  private captureReady = false;
  private captureActive = false;
  private acceptedSequence = 0;
  private sequence = 0;
  private id?: string;
  private work?: Promise<void>;

  constructor(
    sink: CallDiagnosticSink,
    now: () => number = () => performance.now()
  ) {
    this.sink = sink;
    this.now = now;
    this.started = now();
  }

  record(
    stage: CallDiagnosticStage,
    outcome: CallDiagnosticOutcome,
    code: CallDiagnosticCode = 'none',
    durationMs = 0
  ): number | undefined {
    if (stage === 'capture-start' && outcome === 'started') {
      this.capture = [];
      this.captureReady = false;
      this.captureActive = true;
    }
    if (
      this.abort.signal.aborted ||
      this.sequence >= 512 ||
      this.pending.length >= 32 ||
      !stages.includes(stage) ||
      !outcomes.includes(outcome) ||
      !codes.includes(code) ||
      !Number.isFinite(durationMs) ||
      durationMs < 0
    )
      return undefined;
    const elapsed = this.now() - this.started;
    if (!Number.isFinite(elapsed) || elapsed < 0 || elapsed > 86400000)
      return undefined;
    const sequence = ++this.sequence;
    const captureStage =
      stage === 'capture-start' ||
      stage === 'mic-open' ||
      stage === 'permission' ||
      stage === 'capture-stop' ||
      stage === 'transcribe';
    if (captureStage && this.captureActive) {
      if (
        outcome === 'failed' ||
        outcome === 'cancelled' ||
        this.capture.length >= 30
      ) {
        this.capture = [];
        this.captureReady = false;
        this.captureActive = false;
      } else {
        this.capture.push(sequence);
        if (stage === 'transcribe' && outcome === 'succeeded') {
          this.captureReady = true;
          this.captureActive = false;
        }
      }
    }
    // Never spread caller objects or stringify exceptions. This is the entire payload.
    this.pending.push({
      sequence,
      stage,
      outcome,
      code,
      elapsedMs: Math.floor(elapsed),
      durationMs: Math.min(86400000, Math.floor(durationMs)),
    });
    if (!this.work) this.work = Promise.resolve().then(() => this.drain());
    return sequence;
  }

  /** Claim the latest completed capture once, before the next send starts. */
  takeCapture(): readonly number[] {
    const result = this.captureReady ? this.capture : [];
    this.capture = [];
    this.captureReady = false;
    this.captureActive = false;
    return result;
  }

  link(sequence: number, conversationId: string, eventTurnId: number): void {
    if (
      this.abort.signal.aborted ||
      !this.sink.link ||
      this.links.length >= 32 ||
      !Number.isSafeInteger(sequence) ||
      sequence < 1 ||
      sequence > this.sequence ||
      !/^[a-zA-Z0-9_-]{1,64}$/.test(conversationId) ||
      !Number.isSafeInteger(eventTurnId) ||
      eventTurnId < 1
    )
      return;
    this.links.push({ sequence, conversationId, eventTurnId });
    if (!this.work) this.work = Promise.resolve().then(() => this.drain());
  }

  /** Explicitly awaited only by tests/shutdown orchestration, never the call's hot path. */
  async settled(): Promise<void> {
    await this.work;
  }

  stop(): void {
    this.abort.abort();
    this.pending.length = 0;
    this.links.length = 0;
    this.takeCapture();
  }

  private async drain(): Promise<void> {
    try {
      if (this.abort.signal.aborted) return;
      this.id ??= await this.sink.create(this.abort.signal);
      if (!/^[a-fA-F0-9]{32}$/.test(this.id)) {
        this.stop();
        return;
      }
      while (
        (this.pending.length || this.links.length) &&
        !this.abort.signal.aborted
      ) {
        const batch = this.pending.splice(0, 32);
        if (batch.length) {
          await this.sink.append(this.id, batch, this.abort.signal);
          this.acceptedSequence = batch[batch.length - 1].sequence;
        }
        while (this.links.length && !this.abort.signal.aborted) {
          const link = this.links[0];
          if (link.sequence > this.acceptedSequence) break;
          this.links.shift();
          await this.sink.link!(
            this.id,
            link.sequence,
            link.conversationId,
            link.eventTurnId,
            this.abort.signal
          );
        }
      }
    } catch {
      // Deliberately neither log nor retain errors: they may include tokens or speech.
      this.stop();
    } finally {
      this.work = undefined;
    }
  }
}
